import type { CollagePhoto } from './collageTypes'

export type ExportFormat = 'png' | 'jpeg' | 'webp'

const EXPORT_MIME_TYPES: Record<ExportFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
}

export const EXPORT_FILE_EXTENSIONS: Record<ExportFormat, string> = {
  png: 'png',
  jpeg: 'jpg',
  webp: 'webp',
}

type Rect = { x: number; y: number; width: number; height: number }

/** Tile positions as the browser already laid them out (the justified-row packing), relative to the
 * grid container. Reading this instead of duplicating the layout math in canvas coordinates. Takes
 * the container's own rect rather than re-measuring it, since the caller already has it. */
function measureRelativeRects(containerRect: DOMRect, tileEls: Map<string, HTMLElement>): Map<string, Rect> {
  const rects = new Map<string, Rect>()
  for (const [id, el] of tileEls) {
    const r = el.getBoundingClientRect()
    rects.set(id, {
      x: r.left - containerRect.left,
      y: r.top - containerRect.top,
      width: r.width,
      height: r.height,
    })
  }
  return rects
}

/** object-fit: cover math — the source crop rect matching the destination aspect ratio, centered
 * within the source image. Tiles are sized to match each photo's own aspect ratio almost exactly
 * (up to layout rounding), so this only ever trims a sliver, never a visible crop. */
function coverSourceRect(
  img: HTMLImageElement,
  destWidth: number,
  destHeight: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const destAspect = destWidth / destHeight
  const srcAspect = img.naturalWidth / img.naturalHeight
  let sw = img.naturalWidth
  let sh = img.naturalHeight
  if (srcAspect > destAspect) {
    sw = img.naturalHeight * destAspect
  } else {
    sh = img.naturalWidth / destAspect
  }
  return {
    sx: (img.naturalWidth - sw) / 2,
    sy: (img.naturalHeight - sh) / 2,
    sw,
    sh,
  }
}

/**
 * Waits for every registered tile image to finish decoding before export reads its pixels.
 * Without this, a photo whose `<img>` hasn't decoded yet — most likely right after adding it, or
 * after a reorder remounts its tile (see `CollageGrid`'s row keys) — would silently be skipped by
 * `renderCollageToCanvas`'s own `!img.complete` check, producing an export quietly missing a photo
 * the user believed was included. A genuinely broken image still resolves (via the `catch`) so it
 * falls through to that same, now-legitimate skip rather than hanging the export.
 */
export async function waitForImagesReady(imgEls: Map<string, HTMLImageElement>): Promise<void> {
  await Promise.all(
    Array.from(imgEls.values()).map((img) =>
      img.complete && img.naturalWidth > 0 ? undefined : img.decode().catch(() => undefined),
    ),
  )
}

export type RenderCollageParams = {
  container: HTMLElement
  photos: CollagePhoto[]
  tileEls: Map<string, HTMLElement>
  tileImgEls: Map<string, HTMLImageElement>
  backgroundColor: string
  /** Desired output width in pixels; height follows the on-screen grid's aspect ratio. */
  outputWidth: number
}

export function renderCollageToCanvas({
  container,
  photos,
  tileEls,
  tileImgEls,
  backgroundColor,
  outputWidth,
}: RenderCollageParams): HTMLCanvasElement {
  const containerRect = container.getBoundingClientRect()
  if (containerRect.width <= 0 || containerRect.height <= 0) {
    throw new Error('Collage grid has no visible area to export')
  }

  const scale = outputWidth / containerRect.width
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(outputWidth))
  canvas.height = Math.max(1, Math.round(containerRect.height * scale))

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const rects = measureRelativeRects(containerRect, tileEls)

  for (const photo of photos) {
    const rect = rects.get(photo.id)
    const img = tileImgEls.get(photo.id)
    if (!rect || !img || !img.complete || img.naturalWidth === 0) continue

    const dx = rect.x * scale
    const dy = rect.y * scale
    const dw = rect.width * scale
    const dh = rect.height * scale

    const { sx, sy, sw, sh } = coverSourceRect(img, dw, dh)
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)
  }

  return canvas
}

export function downloadCanvas(
  canvas: HTMLCanvasElement,
  format: ExportFormat,
  quality: number,
  filename = 'collage',
): Promise<void> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not encode the collage image'))
          return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${filename}.${EXPORT_FILE_EXTENSIONS[format]}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        resolve()
      },
      EXPORT_MIME_TYPES[format],
      format === 'png' ? undefined : quality,
    )
  })
}
